import type { EstimatorInput } from "../types/index.js";

/**
 * Estimates a recommended contingency percentage based on risk factors in
 * the input (structural/plumbing/electrical unknowns, full gut scope,
 * etc). This is a simple, transparent heuristic - not derived from any
 * Canadian pricing dataset - and is explicitly a placeholder pending real
 * risk-modeling in a later phase.
 *
 * Kept intentionally separate from the pricing engine so it can be
 * unit tested and swapped out without touching pricing logic.
 */
export function estimateContingencyPercent(input: EstimatorInput): number {
  let percent = 10; // baseline contingency

  if (input.scope === "full_gut_renovation" || input.scope === "custom_high_end") {
    percent += 5;
  }

  if (input.workType.structuralChanges === "yes" || input.workType.structuralChanges === "unknown") {
    percent += 5;
  }

  // Major layout changes carry more real-world risk (structural surprises,
  // permitting delays) than a confirmed non-structural wall removal - see
  // packages/core/src/calculation/rules/kitchen.ts.
  if (input.projectType === "kitchen" && input.rawAnswers["kitchen_structural_scope"] === "major_layout_changes") {
    percent += 3;
  }

  // Bathroom/ensuite-specific risk (Phase 20 audit fix - this project type had no
  // dedicated contingency logic at all): a major plumbing relocation (rerouted
  // supply/drain lines, not just fixture swaps) carries real "what's actually
  // behind the wall" uncertainty, matching rules/bathroom.ts's own "contradictory
  // plumbing changes" warning for this same scenario - this applies even when the
  // shared workType.plumbingChanges flag is "no", since the bathroom-specific
  // plumbing scope is the more specific and more relevant signal here.
  // Audit fix (Phase 23): ensuite reuses bathroom's plumbing-scope question under
  // this exact rawAnswers key (see questions/ensuite.ts's own doc comment and the
  // shared "bathroom_shower_scope" check a few lines below in the ensuite block) -
  // this check was gated to "bathroom" only, so an ensuite project with the
  // identical "major relocation" answer got no bump at all. Same risk, same key,
  // both project types now get it - matching the pattern already used for
  // basement/basement_suite's egress check just below.
  if ((input.projectType === "bathroom" || input.projectType === "ensuite") && input.rawAnswers["bathroom_plumbing_scope"] === "major_relocation") {
    percent += 3;
  }

  // Basement-specific risk: a previously-finished space being renovated
  // carries real "what's behind the walls" uncertainty (see
  // rules/basement.ts's "special handling" warnings), and a new/modified
  // egress requirement is inherently unconfirmed until a code review.
  // Audit fix (Phase 20 audit): a basement suite asks its own egress
  // question under a different rawAnswers key ("basement_suite_egress_status" -
  // see rules/basement-suite.ts), so this needs to check that key for
  // basement_suite, not the plain-basement one, which never matches for a suite.
  if (input.projectType === "basement" || input.projectType === "basement_suite") {
    if (input.rawAnswers["basement_current_condition"] === "previously_finished_renovating") {
      percent += 4;
    }
    const egressStatusKey = input.projectType === "basement_suite" ? "basement_suite_egress_status" : "basement_egress_status";
    if (input.rawAnswers[egressStatusKey] === "new_or_modified_required") {
      percent += 3;
    }
  }

  // Basement-suite-specific risk (beyond the shared basement risk above): a new
  // separate entrance is real construction risk (site conditions, whether it can
  // even be located where planned), and unresolved fire separation between the
  // suite and the rest of the house is a genuine unknown until a fire/building
  // inspection - see rules/basement-suite.ts's own verification warnings for both.
  if (input.projectType === "basement_suite") {
    if (input.rawAnswers["basement_suite_separate_entrance"] === "new_entrance_needed") {
      percent += 2;
    }
    if (input.rawAnswers["basement_suite_fire_separation"] === "unknown" || input.rawAnswers["basement_suite_fire_separation"] === undefined) {
      percent += 2;
    }
  }

  // Deck/patio-specific risk: an elevated structure carries real "what does
  // the actual footing/frost depth require" uncertainty until a site/soil
  // assessment (see rules/deck-patio.ts's "special handling" warnings), and
  // "significant engineering required" complexity is inherently unpriced
  // until an engineer's assessment.
  if (input.projectType === "deck_patio") {
    if (input.rawAnswers["deck_patio_kind"] !== "patio" && input.rawAnswers["deck_patio_elevation"] === "elevated") {
      percent += 3;
    }
    if (input.rawAnswers["deck_patio_structural_complexity"] === "significant_engineering_required") {
      percent += 4;
    }
  }

  // Garage-specific risk: a detached garage's electrical upgrade usually
  // means an unassessed trenching/service-run distance (see
  // rules/garage.ts's "special handling" warnings).
  if (input.projectType === "garage") {
    if (input.rawAnswers["garage_attachment"] === "detached" && input.rawAnswers["garage_electrical_upgrade"] && input.rawAnswers["garage_electrical_upgrade"] !== "none") {
      percent += 3;
    }
  }

  // Attic-specific risk: finishing over roof trusses or with marginal
  // headroom carries real engineering/feasibility uncertainty (see
  // rules/attic.ts's "special handling" warnings).
  if (input.projectType === "attic" && input.rawAnswers["attic_finishing_scope"] === "finishing_to_living_space") {
    if (input.rawAnswers["attic_structural_limitations"] === "attic_trusses") {
      percent += 5;
    } else if (input.rawAnswers["attic_structural_limitations"] === "insufficient_headroom") {
      percent += 3;
    }
  }

  // Exterior-specific risk: structural repairs are, by their own warning,
  // highly variable and can't be meaningfully scoped from a questionnaire.
  if (input.projectType === "exterior" && input.rawAnswers["exterior_structural_repairs"] === true) {
    percent += 4;
  }

  // Ensuite-specific risk: a compartmentalized or dual-walkway layout adds real framing/plumbing
  // rerouting complexity beyond a standard single-fixture-path bathroom (see rules/ensuite.ts),
  // and a major-plumbing scope selection (a custom or premium/custom tub/shower solution, which
  // typically means rerouted supply/drain lines, not just fixture swaps) carries the same kind of
  // "what's actually behind the wall" uncertainty bathroom's own plumbing-unknown factor covers.
  if (input.projectType === "ensuite") {
    if (input.rawAnswers["ensuite_layout_complexity"] === "dual_walkway_complex") {
      percent += 3;
    } else if (input.rawAnswers["ensuite_layout_complexity"] === "compartmentalized") {
      percent += 2;
    }
    if (input.rawAnswers["bathroom_shower_scope"] === "custom_shower" || input.rawAnswers["bathroom_shower_scope"] === "premium_custom") {
      percent += 3;
    }
  }

  // Addition-specific risk (Phase 20 audit fix - this project type had no dedicated
  // contingency logic at all): tying a new structure into an existing home's frame/roofline
  // is real "what will we find once we open it up" risk that scales with the complexity
  // tier the user selected (see rules/addition.ts and questions/addition.ts's own framing
  // of addition_structural_complexity as "one of the biggest cost and risk factors"), an
  // unconfirmed foundation type leaves one of the single biggest cost drivers unresolved,
  // and a second storey built OVER an existing structure means the existing structure's
  // load capacity has to be verified before final engineering - a risk a ground-level or
  // free-standing two-storey addition doesn't carry.
  if (input.projectType === "addition") {
    if (input.rawAnswers["addition_structural_complexity"] === "complex") {
      percent += 5;
    } else if (input.rawAnswers["addition_structural_complexity"] === "moderate") {
      percent += 2;
    }
    if (input.rawAnswers["addition_foundation_type"] === "unsure" || input.rawAnswers["addition_foundation_type"] === undefined) {
      percent += 3;
    }
    if (input.rawAnswers["addition_storeys"] === "second_storey_addition") {
      percent += 3;
    }
  }

  // Whole-home-specific risk: an occupied or phased renovation carries real coordination/discovery
  // risk beyond the cost-of-doing-business multiplier already applied to the base line (see
  // rules/whole-home.ts) - phasing in particular means later phases get priced/scoped before
  // earlier phases have actually opened up walls, so more can be discovered mid-project.
  if (input.projectType === "whole_home_renovation") {
    if (input.rawAnswers["whole_home_occupancy_status"] === "occupied") {
      percent += 3;
    } else if (input.rawAnswers["whole_home_occupancy_status"] === "partially_occupied") {
      percent += 2;
    }
    if (input.rawAnswers["whole_home_phasing"] === "phased") {
      percent += 3;
    }
  }

  if (input.workType.plumbingChanges === "unknown" || input.workType.electricalChanges === "unknown") {
    percent += 3;
  }

  if (input.designAndPermits.permitLikelyRequired === "unknown") {
    percent += 2;
  }

  return Math.min(percent, 30);
}
